import {Howl} from 'howler';
import {RNG} from '../utils/rng.js';

export enum Sounds {
    doorOpen = 'doorOpen',
    doorClose = 'doorClose',
    drawerOpen = 'drawerOpen',
    drawerClose = 'drawerClose',
    elevatorStart = 'elevatorStart',
    elevatorStop = 'elevatorStop',
    elevatorLoop = 'elevatorLoop',
    elevatorGateOpen = 'elevatorGateOpen',
    flashLightOn = 'flashLightOn',
    boardHitGround = 'boardHitGround',
    cupboardOpen = 'cupboardOpen',
    boom1 = 'boom1',
    boom4 = 'boom4',

    VOHello = 'VOHello',
    VOHelloooo = 'VOHelloooo',
    VOAiiie = 'VOAiiie',

    patriciaOpening1 = 'patriciaOpening1',
    patriciaOpening2 = 'patriciaOpening2',
    patriciaOpening3 = 'patriciaOpening3',
    patriciaOpening4 = 'patriciaOpening4',
    patriciaOpening5 = 'patriciaOpening5',
    patriciaOpening6 = 'patriciaOpening6',

    MusicNewAreaSpooky = 'MusicNewAreaSpooky',

    LaughingManMedium = 'LaughingManMedium',
    Knife = 'Knife',
    KillerMoveFast = 'KillerMoveFast',
    MoonDroning = 'MoonDroning',
    typeWriter = 'typeWriter',
}

type SoundPreset = {
    [value in Sounds]: string[];
};

const soundsPresets: SoundPreset = {
    doorOpen: ['sfx/door_creaks_1.aac', 'sfx/door_creaks_2.aac', 'sfx/door_creaks_3.aac'],
    doorClose: ['sfx/door_close.aac'],
    drawerOpen: ['sfx/metalDrawer_loop.aac'],
    drawerClose: ['sfx/metalDrawer_loopClose.aac'],
    elevatorStart: ['sfx/elevatorStart.aac'],
    elevatorStop: ['sfx/elevatorStop.aac'],
    elevatorLoop: ['sfx/elevatorLoop.aac'],
    elevatorGateOpen: ['sfx/elevatorGateOpen.aac'],
    flashLightOn: ['sfx/flashlight_on.aac'],
    boardHitGround: [
        'sfx/boardHitGround.aac',
        'sfx/boardHitGround2.aac',
        'sfx/boardHitGround3.aac',
    ],
    cupboardOpen: ['sfx/cupboard_open.aac'],
    boom1: ['sfx/boom_1.aac'],
    boom4: ['sfx/boom_4.aac'],
    VOHello: ['speech/RoseEnglish_Hello.aac'],
    VOHelloooo: ['speech/RoseEnglish_HellooooHello.aac'],
    VOAiiie: ['speech/RoseEnglish_Aiiie.aac'],
    MusicNewAreaSpooky: ['music/mus_newarea_spooky.mp3'],
    patriciaOpening1: ['speech/patricia/Opening1.mp3'],
    patriciaOpening2: ['speech/patricia/Opening2.mp3'],
    patriciaOpening3: ['speech/patricia/Opening3.mp3'],
    patriciaOpening4: ['speech/patricia/Opening4.mp3'],
    patriciaOpening5: ['speech/patricia/Opening5.mp3'],
    patriciaOpening6: ['speech/patricia/Opening6.mp3'],

    LaughingManMedium: ['speech/LaughingManMediumLaugh.aac'],
    Knife: ['sfx/knife_shing_thunk.aac'],
    KillerMoveFast: ['sfx/killer_move_fast.aac'],
    MoonDroning: ['sfx/moonDroning_lp.aac'],
    typeWriter: ['sfx/typewriter1.aac', 'sfx/typewriter2.aac', 'sfx/typewriter3.aac'],
};

type fadeOptions = {
    from: number;
    /**
     * Duration in miliseconds.
     */
    duration: number;
};

/**
 * General manager for playing sound effects.
 */
class SoundManager {
    private sounds: Record<Sounds, Howl[]> = {} as Record<Sounds, Howl[]>;

    initialize(soundsToLoad: string[] = []) {
        if (soundsToLoad.length === 0) {
            soundsToLoad = Object.getOwnPropertyNames(soundsPresets);
        }
        for (const sound of soundsToLoad) {
            const soundsKey = sound as Sounds;
            if (this.sounds[soundsKey] != null) {
                continue;
            }
            this.addSound(soundsKey, soundsPresets[soundsKey]);
        }
    }

    private addSound(name: Sounds, paths: string[]) {
        this.sounds[name] = [];
        for (const path of paths) {
            this.sounds[name].push(
                new Howl({
                    src: path,
                    onloaderror: (error) => console.log('Load Error:', error),
                    onplayerror: (error) => console.log('Play Error:', error),
                })
            );
        }
    }

    /**
     * Plays a sound from the sound manager.
     * @param name Name of the sound to play.
     * @param loop loop the sound; default false.
     * @param volume volume of the sound; default 1.0.
     * @returns the id of the sound played.
     */
    public playSound(
        name: Sounds,
        loop = false,
        volume = 1.0,
        fade: fadeOptions | null = null
    ): number | null {
        let id: number | null = null;

        if (this.sounds[name]) {
            const audio: Howl | null = RNG.getItem(this.sounds[name]);
            if (audio) {
                audio.loop(loop);
                audio.volume(volume);
                id = audio.play();
                if (fade) {
                    audio.fade(fade.from, volume, fade.duration);
                }
            }
        }
        return id;
    }

    /**
     * Stops a sound that is playing
     * @param name Name of the sound to stop.
     */
    public stopSound(name: Sounds, fade: fadeOptions | null = null) {
        this.sounds[name].forEach((sound) => {
            if (fade != null) {
                sound.fade(sound.volume(), 0, fade.duration);
            } else {
                sound.stop();
            }
        });
    }
}

export const SoundManagerInstance = new SoundManager();
